import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Save, Stethoscope, Shield, FileText } from "lucide-react";
import { ROUTES } from "@/routes";

const SPECIALTIES = [
  "Family Medicine",
  "Internal Medicine",
  "Pediatrics",
  "OB/GYN",
  "Psychiatry",
  "Cardiology",
  "Dermatology",
  "Endocrinology",
  "Gastroenterology",
  "Geriatrics",
  "Neurology",
  "Oncology",
  "Orthopedics",
  "Pulmonology",
  "Rheumatology",
  "Urology",
  "General Practice",
  "Nurse Practitioner",
  "Physician Assistant",
  "Licensed Clinical Social Worker",
  "Other",
];

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
];

interface ProfileFormData {
  firstName: string;
  middleName: string;
  lastName: string;
  specialty: string;
  bio: string;
  npiNumber: string;
  taxId: string;
  primaryState: string;
  licenseNumber: string;
  deaNumber: string;
  malpracticeCarrier: string;
  malpracticePolicyNumber: string;
  boardCertification: string;
}

export default function ProviderProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ProfileFormData>({
    firstName: "",
    middleName: "",
    lastName: "",
    specialty: "",
    bio: "",
    npiNumber: "",
    taxId: "",
    primaryState: "",
    licenseNumber: "",
    deaNumber: "",
    malpracticeCarrier: "",
    malpracticePolicyNumber: "",
    boardCertification: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["provider-profile", user?.id],
    queryFn: async () => {
      if (!user) return null;

      const [profileRes, providerRes] = await Promise.all([
        supabase.from("profiles").select("first_name, middle_name, last_name").eq("id", user.id).maybeSingle(),
        (supabase as any).from("providers").select("id, specialty, bio").eq("user_id", user.id).maybeSingle(),
      ]);

      if (!providerRes.data) return { profile: profileRes.data, provider: null, compliance: null, license: null };

      const complianceRes = await (supabase as any)
        .from("provider_compliance")
        .select("*")
        .eq("provider_id", providerRes.data.id)
        .maybeSingle();

      let licenseData = null;
      if (complianceRes.data?.id) {
        const licensesRes = await (supabase as any)
          .from("provider_licenses")
          .select("*")
          .eq("compliance_id", complianceRes.data.id)
          .limit(1)
          .maybeSingle();
        licenseData = licensesRes.data;
      }

      return {
        profile: profileRes.data,
        provider: providerRes.data,
        compliance: complianceRes.data,
        license: licenseData,
      };
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (data) {
      setForm({
        firstName: data.profile?.first_name || "",
        middleName: data.profile?.middle_name || "",
        lastName: data.profile?.last_name || "",
        specialty: data.provider?.specialty || "",
        bio: data.provider?.bio || "",
        npiNumber: data.compliance?.npi_number || "",
        taxId: data.compliance?.tax_id || "",
        primaryState: data.compliance?.primary_state || "",
        licenseNumber: data.license?.license_number || "",
        deaNumber: data.compliance?.dea_number || "",
        malpracticeCarrier: data.compliance?.malpractice_insurance_carrier || "",
        malpracticePolicyNumber: data.compliance?.malpractice_policy_number || "",
        boardCertification: data.compliance?.board_certification || "",
      });
    }
  }, [data]);

  const updateField = (field: keyof ProfileFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      console.log(user, data);
      if (!user || !data?.provider) throw new Error("Provider record not found. Please complete onboarding first.");

      const displayName = [form.firstName, form.middleName, form.lastName].filter(Boolean).join(" ").trim();

      await Promise.all([
        supabase
          .from("profiles")
          .update({
            first_name: form.firstName.trim(),
            middle_name: form.middleName.trim() || null,
            last_name: form.lastName.trim(),
            display_name: displayName,
          } as any)
          .eq("id", user.id),

        (supabase as any)
          .from("providers")
          .update({
            display_name: displayName,
            specialty: form.specialty,
            bio: form.bio.trim() || null,
          })
          .eq("id", data.provider.id),
      ]);

      const compliancePayload = {
        provider_id: data.provider.id,
        npi_number: form.npiNumber.trim(),
        tax_id: form.taxId.trim() || null,
        specialty: form.specialty,
        primary_state: form.primaryState,
        dea_number: form.deaNumber.trim() || null,
        malpractice_insurance_carrier: form.malpracticeCarrier.trim() || null,
        malpractice_policy_number: form.malpracticePolicyNumber.trim() || null,
        board_certification: form.boardCertification.trim() || null,
      };

      if (data.compliance) {
        await (supabase as any).from("provider_compliance").update(compliancePayload).eq("id", data.compliance.id);
      } else {
        await (supabase as any).from("provider_compliance").insert(compliancePayload);
      }

      // Re-fetch compliance id for license upsert
      const { data: freshCompliance } = await (supabase as any)
        .from("provider_compliance")
        .select("id")
        .eq("provider_id", data.provider.id)
        .maybeSingle();

      if (freshCompliance?.id && form.licenseNumber.trim()) {
        const licensePayload = {
          compliance_id: freshCompliance.id,
          state: form.primaryState,
          license_number: form.licenseNumber.trim(),
        };
        if (data.license) {
          await (supabase as any).from("provider_licenses").update(licensePayload).eq("id", data.license.id);
        } else {
          await (supabase as any).from("provider_licenses").insert(licensePayload);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider-profile"] });
      toast({ title: "Profile updated", description: "Your changes have been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  return (
    <div className="flex min-h-screen bg-background">
      <ProviderSidebar />
      <div className="flex-1 ml-64">
        <ProviderHeader />
        <main className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-display font-bold text-foreground">My Profile</h1>
              <p className="text-muted-foreground">View and update your provider information</p>
            </div>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || isLoading}>
              <Save className="h-4 w-4 mr-2" />
              {saveMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : !data?.provider ? (
            <Navigate to={ROUTES.PROVIDER_ONBOARDING} replace />
          ) : (
            <div className="grid gap-6">
              {/* Provider Profile */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Stethoscope className="h-5 w-5 text-primary" />
                    Provider Profile
                  </CardTitle>
                  <CardDescription>Your name, specialty, and bio</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>First Name *</Label>
                      <Input
                        value={form.firstName}
                        onChange={(e) => updateField("firstName", e.target.value)}
                        maxLength={50}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Middle Name</Label>
                      <Input
                        value={form.middleName}
                        onChange={(e) => updateField("middleName", e.target.value)}
                        maxLength={50}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name *</Label>
                      <Input
                        value={form.lastName}
                        onChange={(e) => updateField("lastName", e.target.value)}
                        maxLength={50}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Specialty *</Label>
                    <Select value={form.specialty} onValueChange={(v) => updateField("specialty", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select specialty" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPECIALTIES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Bio</Label>
                    <Textarea
                      value={form.bio}
                      onChange={(e) => updateField("bio", e.target.value)}
                      maxLength={500}
                      rows={3}
                      placeholder="Brief description of your experience..."
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Credentials */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    Credentials
                  </CardTitle>
                  <CardDescription>NPI, Tax ID, and board certification</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>NPI Number (10 digits)</Label>
                      <Input
                        value={form.npiNumber}
                        onChange={(e) => updateField("npiNumber", e.target.value.replace(/\D/g, "").slice(0, 10))}
                        maxLength={10}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tax ID / EIN</Label>
                      <Input value={form.taxId} onChange={(e) => updateField("taxId", e.target.value)} maxLength={20} />
                    </div>
                    <div className="space-y-2">
                      <Label>Primary Practice State</Label>
                      <Select value={form.primaryState} onValueChange={(v) => updateField("primaryState", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Board Certification</Label>
                      <Input
                        value={form.boardCertification}
                        onChange={(e) => updateField("boardCertification", e.target.value)}
                        maxLength={100}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* License & DEA */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    License & DEA
                  </CardTitle>
                  <CardDescription>Medical license and malpractice information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Medical License Number</Label>
                      <Input
                        value={form.licenseNumber}
                        onChange={(e) => updateField("licenseNumber", e.target.value)}
                        maxLength={50}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>DEA Number</Label>
                      <Input
                        value={form.deaNumber}
                        onChange={(e) => updateField("deaNumber", e.target.value)}
                        maxLength={20}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Malpractice Insurance Carrier</Label>
                      <Input
                        value={form.malpracticeCarrier}
                        onChange={(e) => updateField("malpracticeCarrier", e.target.value)}
                        maxLength={100}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Policy Number</Label>
                      <Input
                        value={form.malpracticePolicyNumber}
                        onChange={(e) => updateField("malpracticePolicyNumber", e.target.value)}
                        maxLength={50}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
